/**
 * SearchForm Component
 * Handles keyword and domain input with validation, multiple keywords, and reCAPTCHA
 */
import React, { useState, useRef } from 'react';
import Spinner from './Spinner';
import ReCAPTCHA from 'react-google-recaptcha';

const RECAPTCHA_SITE_KEY = process.env.REACT_APP_RECAPTCHA_SITE_KEY;

const SearchForm = ({ onSearch, loading }) => {
  const [keywords, setKeywords] = useState(['', '', '', '']);
  const [domain, setDomain] = useState('');
  const [useMultipleKeywords, setUseMultipleKeywords] = useState(false);
  const [errors, setErrors] = useState({});
  const recaptchaRef = useRef(null);

  /**
   * Validate form inputs
   */
  const validate = () => {
    const newErrors = {};

    // Get active keywords
    const activeKeywords = useMultipleKeywords 
      ? keywords.filter(k => k.trim())
      : [keywords[0]].filter(k => k.trim());

    if (activeKeywords.length === 0) {
      newErrors.keyword = 'At least one keyword is required';
    } else {
      // Check each keyword length
      activeKeywords.forEach((kw, idx) => {
        if (kw.length > 200) {
          newErrors[`keyword${idx}`] = 'Keyword must be less than 200 characters';
        }
      });
    }

    if (!domain.trim()) {
      newErrors.domain = 'Domain is required';
    } else if (domain.length > 100) {
      newErrors.domain = 'Domain must be less than 100 characters';
    } else if (!domain.includes('.')) {
      newErrors.domain = 'Please enter a valid domain (e.g., example.com)';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  /**
   * Handle form submission
   */
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validate()) {
      return;
    }

    // Get reCAPTCHA token if configured
    let recaptchaToken = null;
    if (RECAPTCHA_SITE_KEY && recaptchaRef.current) {
      recaptchaToken = recaptchaRef.current.getValue();
      
      if (!recaptchaToken) {
        setErrors({ recaptcha: 'Please complete the reCAPTCHA verification' });
        return;
      }
    }

    // Prepare keywords
    const activeKeywords = useMultipleKeywords 
      ? keywords.filter(k => k.trim()).map(k => k.trim())
      : [keywords[0].trim()];

    // Call parent handler
    onSearch({
      keywords: activeKeywords,
      keyword: activeKeywords[0], // For backward compatibility
      domain: domain.trim(),
      recaptchaToken
    });

    // Reset reCAPTCHA
    if (recaptchaRef.current) {
      recaptchaRef.current.reset();
    }
  };

  /**
   * Handle keyword input change
   */
  const handleKeywordChange = (index, value) => {
    const newKeywords = [...keywords];
    newKeywords[index] = value;
    setKeywords(newKeywords);
    
    if (errors[`keyword${index}`] || errors.keyword) {
      const newErrors = { ...errors };
      delete newErrors[`keyword${index}`];
      delete newErrors.keyword;
      setErrors(newErrors);
    }
  };

  /**
   * Clear form and errors
   */
  const handleClear = () => {
    setKeywords(['', '', '', '']);
    setDomain('');
    setUseMultipleKeywords(false);
    setErrors({});
    if (recaptchaRef.current) {
      recaptchaRef.current.reset();
    }
  };

  return (
    <div className="card shadow-xl border-2 border-primary-100 hover:shadow-2xl transition-shadow duration-300">
      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Header with Icon */}
        <div className="text-center pb-4 border-b-2 border-gray-100">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-primary-500 to-primary-600 rounded-full mb-4 shadow-lg">
            <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
          </div>
          <h2 className="text-3xl font-bold bg-gradient-to-r from-primary-600 to-indigo-600 bg-clip-text text-transparent mb-2">
            Check Your Google Ranking
          </h2>
          <p className="text-gray-600 text-base">
            Track where your website ranks on Google search results
          </p>
        </div>

        {/* Domain Input - FIRST */}
        <div className="bg-gradient-to-r from-blue-50 to-indigo-50 p-4 rounded-lg border border-blue-200">
          <label htmlFor="domain" className="flex items-center text-sm font-semibold text-gray-800 mb-3">
            <span className="inline-flex items-center justify-center w-7 h-7 bg-primary-600 text-white rounded-full text-xs mr-2 shadow-md">1</span>
            Target Domain
          </label>
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <svg className="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9m-9 9a9 9 0 019-9" />
              </svg>
            </div>
            <input
              type="text"
              id="domain"
              className={`input-field pl-10 bg-white border-2 ${errors.domain ? 'border-red-500' : 'border-blue-300'} focus:border-primary-500 focus:ring-2 focus:ring-primary-200`}
              placeholder="e.g., example.com or www.yoursite.com"
              value={domain}
              onChange={(e) => {
                setDomain(e.target.value);
                if (errors.domain) {
                  const newErrors = { ...errors };
                  delete newErrors.domain;
                  setErrors(newErrors);
                }
              }}
              disabled={loading}
            />
          </div>
          {errors.domain && (
            <p className="mt-2 text-sm text-red-600 flex items-center">
              <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
              {errors.domain}
            </p>
          )}
          {!errors.domain && (
            <p className="mt-2 text-xs text-gray-600 flex items-center">
              <svg className="w-3 h-3 mr-1 text-blue-500" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
              </svg>
              Enter your website domain (with or without www)
            </p>
          )}
        </div>

        {/* Keyword Inputs - SECOND */}
        <div className="bg-gradient-to-r from-purple-50 to-pink-50 p-4 rounded-lg border border-purple-200">
          <label className="flex items-center text-sm font-semibold text-gray-800 mb-3">
            <span className="inline-flex items-center justify-center w-7 h-7 bg-primary-600 text-white rounded-full text-xs mr-2 shadow-md">2</span>
            Search Keyword{useMultipleKeywords ? 's' : ''}
          </label>
          
          {useMultipleKeywords ? (
            // Multiple keyword inputs
            <div className="space-y-3">
              {keywords.map((keyword, index) => (
                <div key={index}>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <span className="text-xs font-semibold text-gray-500 bg-gray-200 px-2 py-1 rounded">#{index + 1}</span>
                    </div>
                    <input
                      type="text"
                      placeholder={`Enter keyword ${index + 1} ${index === 0 ? '(required)' : '(optional)'}`}
                      value={keyword}
                      onChange={(e) => handleKeywordChange(index, e.target.value)}
                      disabled={loading}
                      className={`input-field pl-16 bg-white border-2 ${errors[`keyword${index}`] ? 'border-red-500' : 'border-purple-300'} focus:border-primary-500 focus:ring-2 focus:ring-primary-200`}
                    />
                  </div>
                  {errors[`keyword${index}`] && (
                    <p className="mt-1 text-sm text-red-600 flex items-center">
                      <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                      </svg>
                      {errors[`keyword${index}`]}
                    </p>
                  )}
                </div>
              ))}
            </div>
          ) : (
            // Single keyword input
            <div>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <svg className="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
                  </svg>
                </div>
                <input
                  type="text"
                  placeholder="e.g., python tutorials, best seo tools"
                  value={keywords[0]}
                  onChange={(e) => handleKeywordChange(0, e.target.value)}
                  disabled={loading}
                  className={`input-field pl-10 bg-white border-2 ${errors.keyword ? 'border-red-500' : 'border-purple-300'} focus:border-primary-500 focus:ring-2 focus:ring-primary-200`}
                />
              </div>
              {errors.keyword && (
                <p className="mt-2 text-sm text-red-600 flex items-center">
                  <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                  </svg>
                  {errors.keyword}
                </p>
              )}
            </div>
          )}

          {/* Multiple Keywords Checkbox - Under Keywords */}
          <div className="mt-4 pt-4 border-t border-purple-200">
            <div className="flex items-center bg-white p-3 rounded-lg border border-purple-300 hover:border-purple-400 transition-colors duration-200 cursor-pointer">
              <input
                type="checkbox"
                id="useMultipleKeywords"
                checked={useMultipleKeywords}
                onChange={(e) => setUseMultipleKeywords(e.target.checked)}
                disabled={loading}
                className="w-5 h-5 text-primary-600 border-gray-300 rounded focus:ring-primary-500 focus:ring-2 cursor-pointer"
              />
              <label htmlFor="useMultipleKeywords" className="ml-3 flex-1 cursor-pointer">
                <span className="text-sm font-semibold text-gray-800">Use multiple keywords</span>
                <p className="text-xs text-gray-600 mt-1">Check up to 4 keywords at once and compare rankings</p>
              </label>
              <div className="flex items-center px-3 py-1 bg-primary-100 text-primary-700 rounded-full text-xs font-bold">
                UP TO 4
              </div>
            </div>
          </div>
        </div>

        {/* reCAPTCHA */}
        {RECAPTCHA_SITE_KEY && (
          <div className="flex flex-col items-center p-4 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
            <p className="text-sm font-medium text-gray-700 mb-3">Security Verification</p>
            <ReCAPTCHA
              ref={recaptchaRef}
              sitekey={RECAPTCHA_SITE_KEY}
              onChange={() => {
                if (errors.recaptcha) {
                  const newErrors = { ...errors };
                  delete newErrors.recaptcha;
                  setErrors(newErrors);
                }
              }}
            />
            {errors.recaptcha && (
              <p className="mt-2 text-sm text-red-600 flex items-center">
                <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                </svg>
                {errors.recaptcha}
              </p>
            )}
          </div>
        )}

        {/* Buttons */}
        <div className="flex gap-4 pt-2">
          <button
            type="submit"
            className="btn-primary flex-1 flex items-center justify-center gap-2 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 py-3 text-base font-semibold"
            disabled={loading}
          >
            {loading ? (
              <>
                <Spinner size="sm" />
                <span>Analyzing Rankings...</span>
              </>
            ) : (
              <>
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
                <span>Check Ranking Now</span>
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                </svg>
              </>
            )}
          </button>
          
          <button
            type="button"
            onClick={handleClear}
            className="px-6 py-3 border-2 border-gray-300 rounded-lg text-gray-700 font-semibold hover:bg-gray-100 hover:border-gray-400 transition-all duration-200 disabled:opacity-50 shadow-md hover:shadow-lg"
            disabled={loading}
          >
            <svg className="w-5 h-5 inline mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
            Reset
          </button>
        </div>

        {/* Help Text */}
        <div className="mt-4 p-4 bg-blue-50 border-l-4 border-blue-400 rounded-r-lg">
          <div className="flex items-start">
            <svg className="w-5 h-5 text-blue-600 mt-0.5 mr-2 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
            </svg>
            <div className="text-sm text-blue-800">
              <p className="font-semibold mb-1">💡 Pro Tip:</p>
              <p>We scan the top 50 Google search results to find your domain's ranking position. Results are based on real-time data.</p>
            </div>
          </div>
        </div>
      </form>
    </div>
  );
};

export default SearchForm;
